import { useEffect } from "react";
import { useRouteError, isRouteErrorResponse, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { isChunkError, reloadOnce, hardReload } from "@/lib/chunkReload";

/**
 * Friendly errorElement for the root route: prevents React Router's black
 * developer error screen from ever reaching visitors.
 */
export const RouteErrorBoundary = () => {
  const error = useRouteError();

  useEffect(() => {
    if (isChunkError(error)) reloadOnce();
  }, [error]);

  let title = "Etwas ist schiefgelaufen";
  let description =
    "Die Seite konnte nicht vollständig geladen werden. Bitte lade die Seite neu.";

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = "Seite nicht gefunden";
      description = "Diese Seite existiert nicht oder wurde verschoben.";
    } else {
      title = `Fehler ${error.status}`;
      description = error.statusText || description;
    }
  }

  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? `${error.name}: ${error.message}`
      : String(error ?? "Unbekannt");

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <AlertTriangle className="w-12 h-12 text-destructive mx-auto" aria-hidden="true" />
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          <p className="text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => hardReload()} className="gap-2">
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            Seite neu laden
          </Button>
          <Button asChild variant="outline" className="gap-2">
            <Link to="/">
              <Home className="w-4 h-4" aria-hidden="true" />
              Zur Startseite
            </Link>
          </Button>
        </div>
        <details className="text-left text-xs text-muted-foreground">
          <summary className="cursor-pointer text-center">Technische Details</summary>
          <pre className="mt-2 whitespace-pre-wrap break-words rounded-md border border-border bg-muted p-3">
            {detail}
          </pre>
        </details>
      </div>
    </main>
  );
};
